import { useMemo } from 'react';
import { Tree } from 'react-arborist';
import type { VoidFunctionComponent } from 'react';
import type { NodeApi } from 'react-arborist';

import { useVendoredDependencyPaths } from '../../../../hooks/api/vendoredDependencies';
import { TreeViewLayout } from '../../shared/TreeViewLayout';
import { formatPathForId } from '../../snippets/utils';

import buildTree from './buildTree';
import TreeViewLoader from './TreeViewLoader';
import VendoredDependenciesTreeViewNode from './VendoredDependenciesTreeViewNode';
import type { VendoredDependencyTreeNode } from './buildTree';

const TREE_WIDTH = 250;

type Props = {
  /**
   * The user project revision locator.
   */
  userRevisionLocator: string;
  /**
   * The currently selected path in the tree.
   */
  selectedPath: string | null;
  /**
   * Function to set the selected path in the tree.
   */
  setSelectedPath: (path: string | null) => void;
};

/**
 * Tree view for the vendored dependencies.
 */
const VendoredDependenciesTreeView: VoidFunctionComponent<Props> = ({
  userRevisionLocator,
  selectedPath,
  setSelectedPath,
}) => {
  const { data: paths, isLoading } = useVendoredDependencyPaths(userRevisionLocator);

  //  Transform flat paths array into hierarchical tree structure
  const treeData = useMemo(() => {
    //  Vendored deps always appear in a directory, so we always want to include a trailing slash.
    const shouldIncludeTrailingSlash = true;
    if (!paths?.paths) {
      return [];
    }
    return buildTree(paths.paths, shouldIncludeTrailingSlash);
  }, [paths]);

  return (
    <TreeViewLayout.TreeView header="Files">
      {isLoading ? (
        <TreeViewLoader />
      ) : (
        <Tree<VendoredDependencyTreeNode>
          data={treeData}
          openByDefault={false}
          //  To determine the react-arborist internal ID for this node,
          //  we need to ensure that the trailing slash on directories is correct.
          idAccessor={(node) => formatPathForId(node.path, node.type)}
          childrenAccessor="children"
          //  Layout
          rowHeight={32}
          indent={8}
          width={TREE_WIDTH}
          //  Selection logic -- This will be added as part of the next ticket (CORE-5376)
          selection={selectedPath || undefined}
          onSelect={(nodes: Array<NodeApi<VendoredDependencyTreeNode>>) => {
            //  react-arborist allows mutli-select, which is why it passes us an array of nodes.
            //  We only want to select the first node, so we take the first one.
            const node = nodes[0];
            if (node) {
              setSelectedPath(node.data.path);
            }
          }}
          disableMultiSelection
          disableDrag
        >
          {VendoredDependenciesTreeViewNode}
        </Tree>
      )}
    </TreeViewLayout.TreeView>
  );
};

export default VendoredDependenciesTreeView;
